import React from 'react';
import { Calculator, FileText, Share2, ChevronRight } from 'lucide-react';

const ProductCoverage: React.FC = () => {
  const coverageAreas = [
    {
      icon: Calculator,
      title: "Quant",
      color: "blue",
      items: [
        "Market Risk / VaR",
        "SRI/SRRI",
        "Benchmark Performance",
        "Past Performance / Future Performance",
        "Costs calculations including transaction costs",
        "SCR calculations",
        "Private Equity/ELTIF Model for PRIIPs",
        "Risk & Global exposure for AIFs under CESR 10-788",
        "RWA/CVA under CRR"
      ]
    },
    {
      icon: FileText,
      title: "Regulation",
      color: "green",
      items: [
        "PRIIPs/MiFID/UCITS (EU)",
        "Solvency II",
        "CSSF Circular 11/512",
        "PRIIPs/UCITS (UK)",
        "AIFMD annex IV",
        "ESG: SFDR and SDR",
        "CSSF SFDR UC Data collection",
        "CRR Regulation"
      ]
    },
    {
      icon: Share2,
      title: "Reporting & Dissemination",
      color: "purple",
      items: [
        "Solvency II TPT / FinXn",
        "EPT, CEPT, LPT, EMT",
        "SFDR Annexes including PAIs",
        "CSSF SFDR / EET (European ESG template)",
        "Consob PRIIPs Reporting",
        "Report Dissemination to any vendor or client institutional investors",
        "CSSF API filing"
      ]
    }
  ];

  const getColorClasses = (color: string) => {
    switch (color) {
      case 'blue':
        return {
          iconBg: 'bg-blue-900 bg-opacity-50',
          iconColor: 'text-blue-400',
          accent: 'text-blue-400'
        };
      case 'green':
        return {
          iconBg: 'bg-green-900 bg-opacity-50',
          iconColor: 'text-green-400',
          accent: 'text-green-400'
        };
      case 'purple':
        return {
          iconBg: 'bg-purple-900 bg-opacity-50',
          iconColor: 'text-purple-400',
          accent: 'text-purple-400'
        };
      default:
        return {
          iconBg: 'bg-blue-900 bg-opacity-50',
          iconColor: 'text-blue-400',
          accent: 'text-blue-400'
        };
    }
  };

  return (
    <section id="product-coverage" className="py-12 sm:py-16 lg:py-20 bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12 lg:mb-16">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4">
            Comprehensive Regulatory Reporting Product Coverage
          </h2>
          <p className="text-lg sm:text-xl text-gray-300 max-w-3xl mx-auto">
            From quantitative computations to regulatory compliance and reporting dissemination, 
            KEOPS covers every aspect of modern fund risk management.
          </p>
        </div>

        {/* Coverage Areas */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
          {coverageAreas.map((area, index) => {
            const IconComponent = area.icon;
            const colors = getColorClasses(area.color);
            
            return (
              <div 
                key={index}
                className="bg-gray-800 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 p-6 lg:p-8 border border-gray-700"
              >
                {/* Icon and Title */}
                <div className="flex items-center mb-6">
                  <div className={`flex items-center justify-center w-12 h-12 lg:w-14 lg:h-14 ${colors.iconBg} rounded-lg mr-4`}>
                    <IconComponent className={`w-6 h-6 lg:w-7 lg:h-7 ${colors.iconColor}`} />
                  </div>
                  <h3 className="text-xl lg:text-2xl font-bold text-white">
                    {area.title}
                  </h3>
                </div>

                {/* Items List */}
                <div className="space-y-3">
                  {area.items.map((item, itemIndex) => (
                    <div 
                      key={itemIndex}
                      className="flex items-start space-x-3 group"
                    >
                      <ChevronRight className={`w-4 h-4 ${colors.accent} mt-0.5 flex-shrink-0 transition-transform duration-200 group-hover:translate-x-1`} />
                      <span className="text-gray-300 text-sm lg:text-base leading-relaxed group-hover:text-white transition-colors duration-200">
                        {item}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Coverage Count */}
                <div className="mt-6 pt-4 border-t border-gray-700">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400 text-sm">And much more...</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

      </div>
    </section>
  );
};

export default ProductCoverage;
